/** Maximum character count for the headline field */
export const HEADLINE_MAX_LENGTH = 150;

/** Page size for the taught-profile-courses endpoint */
export const COURSES_PAGE_SIZE = 10;

/** Maximum number of skills an instructor can add */
export const MAX_SKILLS = 6;

/** Minimum number of skills required on the profile */
export const MIN_SKILLS = 3;

/** Maximum character count for a skill name */
export const SKILL_NAME_MAX_LENGTH = 35;

/**
 * Expressive icon names available for skill icon selection.
 * Each value is a valid `ExpressiveIconName` from `@udemy-v2/icons-expressive`
 * and is stored directly as `icon_title` in the backend.
 */
export const SKILL_ICON_OPTIONS = [
    'ai',
    'approachable-content',
    'assessment-quiz',
    'capstone-events',
    'challenge-1',
    'change-management',
    'choosing-hand',
    'co-hort',
    'communication',
    'content',
    'course-builder',
    'course-ratings',
    'data-science',
    'desktop',
    'devops',
    'digital-transformation',
    'empty-state-1',
    'executive-briefing',
    'global-1',
    'hands-on-learning',
    'hobby',
    'impact-measurement',
    'instructor',
    'leadership',
    'leading-high-performance-teams',
    'leading-through-change',
    'learner',
    'learning-guide',
    'learning-labs',
    'learning-path',
    'learning-together',
    'marketing',
    'mental-health',
    'mentor',
    'micro-learning',
    'mobile',
    'new',
    'new-manager',
    'on-demand-1',
    'operations-management',
    'organizations-1',
    'privacy',
    'productivity',
    'project-management',
    'qa',
    'quality',
    'relevance-1',
    'remote-work',
    'roi',
    'rookie',
    'sales-target',
    'smart-tips',
    'storytelling',
    'streak-flame',
    'team-training-1',
    'team-training-2',
    'trailblazer',
    'translate-2',
    'users',
] as const;

export type SkillIconCategory = (typeof SKILL_ICON_OPTIONS)[number];

export const SKILL_ICON_BATCH_SIZE = 6;

/** Social link URL prefixes displayed as non-editable addons */
export const SOCIAL_LINK_PREFIXES: Record<string, string> = {
    facebook_profile: 'facebook.com/',
    instagram_profile: 'instagram.com/',
    linkedin_profile: 'linkedin.com/',
    tiktok_profile: 'tiktok.com/',
    twitter_profile: 'x.com/',
    youtube_profile: 'youtube.com/',
};

/** Allowed image extensions for photo upload */
export const ALLOWED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.gif', '.png'];

/** Minimum width (pixels) required for the instructor cover photo. */
export const COVER_PHOTO_MIN_WIDTH = 1440;

/** Minimum height (pixels) required for the instructor cover photo. */
export const COVER_PHOTO_MIN_HEIGHT = 488;

/** Aspect ratio (width / height) used when cropping the instructor cover photo. */
export const COVER_PHOTO_ASPECT = COVER_PHOTO_MIN_WIDTH / COVER_PHOTO_MIN_HEIGHT;

/** Minimum width (pixels) required for the instructor mobile cover photo. */
export const MOBILE_COVER_PHOTO_MIN_WIDTH = 960;

/** Minimum height (pixels) required for the instructor mobile cover photo. */
export const MOBILE_COVER_PHOTO_MIN_HEIGHT = 860;

/** Aspect ratio (width / height) used when cropping the instructor mobile cover photo. */
export const MOBILE_COVER_PHOTO_ASPECT =
    MOBILE_COVER_PHOTO_MIN_WIDTH / MOBILE_COVER_PHOTO_MIN_HEIGHT;

/** Minimum width (pixels) required for the community hero photo. */
export const COMMUNITY_PHOTO_MIN_WIDTH = 1280;

/** Minimum height (pixels) required for the community hero photo. */
export const COMMUNITY_PHOTO_MIN_HEIGHT = 800;

/** Aspect ratio (width / height) used when cropping the community hero photo. */
export const COMMUNITY_PHOTO_ASPECT =
    COMMUNITY_PHOTO_MIN_WIDTH / COMMUNITY_PHOTO_MIN_HEIGHT;

/** Minimum width (pixels) required for a feature-slot custom image. */
export const FEATURE_IMAGE_MIN_WIDTH = 1280;

/** Minimum height (pixels) required for a feature-slot custom image. */
export const FEATURE_IMAGE_MIN_HEIGHT = 800;

/** Aspect ratio (width / height) used when cropping a feature-slot custom image. */
export const FEATURE_IMAGE_ASPECT = FEATURE_IMAGE_MIN_WIDTH / FEATURE_IMAGE_MIN_HEIGHT;

/**
 * Field set requested from GET /api-2.0/users/instructor-profile/me/.
 * Shared between the server fetcher ([profile-api.server.ts]) and client
 * refetchers ([profile-api.ts]) so SSR and any subsequent client refetches
 * always resolve to the exact same InstructorProfile shape.
 */
export const INSTRUCTOR_PROFILE_FIELDS = [
    // `id` powers the client-fetched All Courses / Reviews sections in the
    // instructor preview (which reuses the public profile components).
    'id',
    'name',
    'surname',
    'job_title',
    'description',
    'locale',
    'url_personal_website',
    'facebook_profile',
    'instagram_profile',
    'linkedin_profile',
    'tiktok_profile',
    'twitter_profile',
    'youtube_profile',
    'is_published_instructor',
    'is_ufb_profile_visible',
    'is_ufb_profile_editable',
    'is_scim_managed',
    'cover_photo_960x400',
    'cover_photo_1080x365',
    'cover_photo_1280x433',
    'cover_photo_1440x487',
    'mobile_cover_photo_960x860',
    'skills',
    'featured_courses',
    'video_intro_url',
    'video_intro_thumbnail_url',
] as const;
